import { useMemo } from 'react';
import type { Order } from '../../types/ordering.types';
import type { Person } from '../../types/timesheet.types';
import {
  OrderListTable,
  COL_REF, COL_DATE, COL_DESC, COL_CATEGORY,
  COL_EXP_DELIVERY, COL_TOTAL, COL_STATUS, COL_REQUESTER,
} from './OrderListTable';

interface Props {
  orders: Order[];
  isLoading: boolean;
  onOpenOrder: (order: Order) => void;
  search: string;
  currentPerson: Person;
}

export function ReviewedQueue({ orders, isLoading, onOpenOrder, search, currentPerson }: Props) {
  const email = currentPerson.m365Email.toLowerCase();

  const filtered = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.trim().toLowerCase();
    return orders.filter(o =>
      o.title.toLowerCase().includes(q) ||
      o.orderRef.toLowerCase().includes(q) ||
      o.requesterName.toLowerCase().includes(q)
    );
  }, [orders, search]);

  // Determine which role(s) this user played on each order (for the info banner)
  const roleSummary = useMemo(() => {
    let asLM = 0, asCFO = 0, asChem = 0, asOM = 0;
    for (const o of orders) {
      if (o.lineManagerEmail.toLowerCase() === email) asLM++;
      if (o.cfoApproverEmail && o.cfoApproverEmail.toLowerCase() === email) asCFO++;
      if (currentPerson.isChemicalApprover && o.hasChemicalLines) asChem++;
      if (currentPerson.isOrderMgmtGroup) asOM++;
    }
    const parts: string[] = [];
    if (asLM   > 0) parts.push(`${asLM} as Line Manager`);
    if (asCFO  > 0) parts.push(`${asCFO} as CFO`);
    if (asChem  > 0) parts.push(`${asChem} as Chemical Approver`);
    if (asOM    > 0) parts.push(`${asOM} as OM Member`);
    return parts.join(' · ');
  }, [orders, email, currentPerson]);

  const columns = [
    COL_REF(), COL_DATE('orderDate'), COL_DESC(), COL_CATEGORY(),
    COL_EXP_DELIVERY(), COL_TOTAL(), COL_STATUS(), COL_REQUESTER(),
  ];

  return (
    <div className="ord-view-content">
      <div className="info-box blue">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
        <span>
          <strong>{orders.length} order{orders.length !== 1 ? 's' : ''}</strong> you have reviewed or actioned.
          {roleSummary && <> &nbsp;({roleSummary})</>}
          &nbsp;Read-only — click an order to view the full record.
        </span>
      </div>

      {isLoading ? (
        <div className="ord-empty"><div className="spinner" /> Loading reviewed orders…</div>
      ) : (
        <OrderListTable
          orders={filtered}
          columns={columns}
          onRowClick={onOpenOrder}
          emptyMessage="No orders found in your review history."
        />
      )}
    </div>
  );
}
